import { useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  Checkbox,
  Input,
  RadioGroup,
  SignaturePad,
  apiFetch,
  type ActionLinkContext,
  type ActionLinkSignBlockedReason,
  type AlertTone,
  type ActionLinkSignRequest,
} from "@dearbill/shared";
import { openPendingTab } from "../pendingTab";
import { typedSignaturePngBase64 } from "./typedSignature";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

const SUCCESS_MESSAGE = "You're all set — you can hang up the phone or close this page.";

/**
 * Why the page shows a note instead of the signing form. `signed` is a
 * known signature (`signBlockedReason`, the consent status or a 409 on
 * submit). `notApproved` and `incomplete` are the sign link's other blocked
 * reasons. `unavailable` is a sign link with `signAllowed` false and no
 * reason, so the copy claims neither signed nor not ready. `expired` is a
 * lapsed consent the server will not re-sign. `beneficiaryOnly` is a consent
 * link the holder may not sign (`signAllowed` false while unsigned, or a 403
 * on submit): the CMS-1696 is signed by the beneficiary, not a caregiver.
 */
type Notice =
  | "signed"
  | "notApproved"
  | "incomplete"
  | "unavailable"
  | "expired"
  | "beneficiaryOnly";

const BLOCKED_REASON_NOTICE: Record<ActionLinkSignBlockedReason, Notice> = {
  signed: "signed",
  not_approved: "notApproved",
  incomplete: "incomplete",
};

const NOTICE_COPY: Record<Notice, { tone: AlertTone; title: string; body: string }> = {
  signed: {
    tone: "success",
    title: "Already signed",
    body: "Thank you. This form is signed, so there's nothing else to do here.",
  },
  notApproved: {
    tone: "info",
    title: "This form isn't ready to sign yet",
    body: "We'll text you when it's ready for your signature.",
  },
  incomplete: {
    tone: "info",
    title: "This form isn't ready to sign yet",
    body: "We still need a few details to finish your form. We'll reach out to you, then text you when it's ready for your signature.",
  },
  unavailable: {
    tone: "info",
    title: "Nothing to sign right now",
    body: "This form isn't ready for your signature yet, or it's already signed. If it still needs your signature, we'll let you know when it's ready.",
  },
  expired: {
    tone: "info",
    title: "This form has expired",
    body: "Your Appointment of Representative form needs to be signed again. Please contact us and we'll send you a new one.",
  },
  beneficiaryOnly: {
    tone: "info",
    title: "This form must be signed by the beneficiary",
    body: "The Appointment of Representative form has to be signed by the person with Medicare themselves. Please ask them to open the link we sent to their phone, or contact us for help.",
  },
};

const DEFAULT_INTENT =
  "I intend to sign this form electronically and agree to do business electronically.";

const GENERIC_SUBMIT_ERROR = "We couldn't record your signature. Please try again.";

const UNAVAILABLE_SUBMIT_ERROR = "Signing is unavailable right now, please try again later.";

/** The note the context calls for up front, or null when the form is signable. */
function initialNotice(action: "sign" | "consent", context: ActionLinkContext): Notice | null {
  if (action === "sign") {
    if (context.signBlockedReason) return BLOCKED_REASON_NOTICE[context.signBlockedReason];
    return context.signAllowed === false ? "unavailable" : null;
  }
  if (context.consentStatus === "signed") return "signed";
  // Unsigned (or lapsed) but not signable: the holder is not an allowed signer.
  if (context.signAllowed === false) return "beneficiaryOnly";
  // An expired consent is re-signable only where the server says so with
  // `signAllowed`; a context without it comes from a server whose sign
  // endpoint rejects the re-sign.
  if (context.consentStatus === "expired" && context.signAllowed !== true) return "expired";
  return null;
}

/** Per-action copy and endpoints; sign and consent share one signing flow. */
const ACTION_COPY = {
  sign: {
    formTitle: "Medicare Prescription Payment Plan enrollment form (CMS-10882)",
    documentPath: "/api/links/sign/document",
    signPath: "/api/links/sign",
    documentFilename: "cms-10882.pdf",
    intro: null as string | null,
  },
  consent: {
    formTitle: "Appointment of Representative (CMS-1696)",
    documentPath: "/api/links/consent/document",
    signPath: "/api/links/consent/sign",
    documentFilename: "cms-1696.pdf",
    intro: "This form lets DearBill work with your insurance plan on your behalf.",
  },
} as const;

/**
 * How the member signs. `draw` uses the pointer-only canvas; `type` adopts
 * the typed legal name as the signature, the keyboard and screen-reader path.
 */
type SignMethod = "draw" | "type";

const SIGN_METHOD_OPTIONS: { value: SignMethod; label: string }[] = [
  { value: "draw", label: "Draw my signature" },
  { value: "type", label: "Use my typed name as my signature" },
];

/** "a", "a and b", "a, b and c": the house list style, without an Oxford comma. */
function joinMissing(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

interface ActionLinkSignProps {
  /** The action-link bearer token, replayed on every call; never in a URL. */
  token: string;
  /** The loaded link context; `action` is `sign` or `consent` here. */
  context: ActionLinkContext;
  /** Called when a call is rejected with the opaque 401 (the link died mid-flow). */
  onExpired: () => void;
  /** Called once the form is signed (or found already signed); the link is used up. */
  onFinished: () => void;
}

/**
 * The mid-call signing flow for a sign or consent link: greeting, a
 * "Review the form" link that fetches the prefilled PDF as a bearer-authed
 * blob and opens it (the token never appears in a URL), then the signature,
 * typed name and intent checkbox, POSTed to the action's sign route.
 *
 * The signature is drawn on the canvas or, for keyboard and screen-reader
 * users, adopted from the typed name, which `typedSignaturePngBase64`
 * renders to the same PNG field the drawn one fills. While the submit button
 * is disabled, a hint tied to it with `aria-describedby` lists what is
 * still missing.
 *
 * The context's `signAllowed` / `consentStatus` gates render a `Notice` up
 * front instead of a dead submit; a racing 409 on submit lands on the
 * already-signed note. A consent link shows the server's `intentText` as the
 * intent label and echoes its `consentVersion` in the sign request, which the
 * consent sign requires to match.
 */
export function ActionLinkSign({
  token,
  context,
  onExpired,
  onFinished,
}: ActionLinkSignProps): ReactNode {
  const action = context.action === "consent" ? "consent" : "sign";
  const copy = ACTION_COPY[action];

  const [typedName, setTypedName] = useState("");
  const [intent, setIntent] = useState(false);
  const [method, setMethod] = useState<SignMethod>("draw");
  const [hasStroke, setHasStroke] = useState(false);
  const missingId = useId();
  const getPngRef = useRef<() => string>(() => "");
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(() => initialNotice(action, context));

  const greeting = context.patientFirstName ? `Hi ${context.patientFirstName}!` : "Hi there!";

  if (done) {
    return (
      <div role="status" className="flex flex-col gap-4">
        <h1 className="text-h3 text-midnight font-serif">Thank you!</h1>
        <p className="text-body-lg text-midnight">{SUCCESS_MESSAGE}</p>
      </div>
    );
  }

  if (notice !== null) {
    const { tone, title, body } = NOTICE_COPY[notice];
    return (
      <Alert tone={tone} title={title}>
        {body}
      </Alert>
    );
  }

  const openDocument = (): void => {
    // Reserved before the fetch, while the tap still counts as a user gesture.
    const tab = openPendingTab();
    setOpening(true);
    setSubmitError(null);
    apiFetch<Blob>(copy.documentPath, {}, { baseUrl: API_BASE, token, responseType: "blob" })
      .then((blob) => {
        // An object URL in a new tab: the simplest full-screen PDF read on a
        // phone, and the bearer token stays out of the URL.
        tab.navigate(URL.createObjectURL(blob));
      })
      .catch((err: unknown) => {
        tab.close();
        if (err instanceof ApiError && err.status === 401) {
          onExpired();
          return;
        }
        setSubmitError("We couldn't open the form. Please try again.");
      })
      .finally(() => setOpening(false));
  };

  const name = typedName.trim();
  const missing: string[] = [];
  if (method === "draw" && !hasStroke) missing.push("draw your signature");
  if (name.length === 0) missing.push("type your full legal name");
  if (!intent) missing.push("check the box to agree to sign electronically");
  const canSubmit = missing.length === 0 && !busy;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const signaturePngBase64 =
        method === "type" ? await typedSignaturePngBase64(name) : getPngRef.current();
      await apiFetch(
        copy.signPath,
        {
          method: "POST",
          body: {
            signaturePngBase64,
            typedName: name,
            intent: true,
            ...(action === "consent" && context.consentVersion
              ? { consentVersion: context.consentVersion }
              : {}),
          } satisfies ActionLinkSignRequest,
        },
        { baseUrl: API_BASE, token },
      );
      onFinished();
      setDone(true);
    } catch (err: unknown) {
      setBusy(false);
      if (err instanceof ApiError) {
        if (err.status === 401) {
          onExpired();
          return;
        }
        if (err.status === 409) {
          onFinished();
          setNotice("signed");
          return;
        }
        if (err.status === 403 && action === "consent") {
          setNotice("beneficiaryOnly");
          return;
        }
        if (err.status === 503) {
          setSubmitError(UNAVAILABLE_SUBMIT_ERROR);
          return;
        }
      }
      setSubmitError(GENERIC_SUBMIT_ERROR);
    }
  };

  return (
    <div>
      <h1 className="text-h3 text-midnight font-serif">{greeting}</h1>
      {copy.intro !== null && <p className="text-body text-midnight mt-3">{copy.intro}</p>}
      {action === "consent" && context.consentStatus === "expired" && (
        <p className="text-body text-midnight mt-3">
          Your earlier form has expired, so please sign it again.
        </p>
      )}
      {action === "sign" && context.enrollmentSummary && (
        <p className="text-body text-midnight mt-3">
          {context.enrollmentSummary.planName
            ? `You're signing your enrollment form for ${context.enrollmentSummary.planName}.`
            : "You're signing your enrollment form."}
        </p>
      )}

      <div className="mt-6">
        <p className="text-body-sm text-slate-teal">
          {copy.formTitle}.{" "}
          <button
            type="button"
            onClick={openDocument}
            disabled={opening}
            className="text-primary-text cursor-pointer font-semibold underline hover:no-underline"
          >
            {opening ? "Opening…" : "Review the form"}
          </button>
        </p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={(e) => void onSubmit(e)} noValidate>
        <RadioGroup
          legend="How would you like to sign?"
          name="signMethod"
          options={SIGN_METHOD_OPTIONS}
          value={method}
          onChange={(e) => {
            setMethod(e.target.value === "type" ? "type" : "draw");
            // The pad remounts blank when the member switches back to drawing.
            setHasStroke(false);
          }}
        />

        {method === "draw" && (
          <SignaturePad
            label="Draw your signature"
            clearLabel="Clear"
            ariaLabel="Signature drawing area"
            onChange={({ hasStroke: hs, toPngBase64 }) => {
              setHasStroke(hs);
              getPngRef.current = toPngBase64;
            }}
          />
        )}

        <Input
          label="Type your full legal name"
          required
          autoComplete="name"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
        />

        <Checkbox
          label={
            action === "consent" && context.intentText ? (
              <span className="whitespace-pre-line">{context.intentText}</span>
            ) : (
              DEFAULT_INTENT
            )
          }
          checked={intent}
          onChange={(e) => setIntent(e.target.checked)}
        />

        {method === "type" && name.length > 0 && (
          <div>
            <p className="text-body-sm text-slate-teal">Your signature will look like this:</p>
            <p
              aria-hidden="true"
              className="border-haze text-num-lg text-midnight mt-2 break-words rounded-2xl border bg-white px-5 py-4 font-serif"
            >
              {name}
            </p>
          </div>
        )}

        {submitError && <Alert tone="error" title={submitError} />}

        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={!canSubmit}
          aria-describedby={missing.length > 0 ? missingId : undefined}
        >
          {busy ? "Submitting…" : "Sign and submit"}
        </Button>
        {missing.length > 0 && (
          <p id={missingId} className="text-body-sm text-slate-teal">
            To submit, {joinMissing(missing)}.
          </p>
        )}
      </form>
    </div>
  );
}
